import React, { useState } from 'react';

const RoundTripFlightCard = ({ flight, outboundFlight, returnFlight, onBook, lastSearchParams, isBestDeal = false }) => {
  const [showAllOptions, setShowAllOptions] = useState(false);
  
  // Handle both API field names: 'return' and 'returnFlight'
  const outbound = flight?.outboundFlight || outboundFlight || flight?.outbound || flight;
  const returnF = flight?.returnFlight || returnFlight || flight?.return || null;
  
  // Get passenger count from lastSearchParams
  const passengers = lastSearchParams?.passengers || lastSearchParams?.adults || flight?.passengers || 1;
  
  // Price handling - prefer perPersonPrice, fallback to calculating from total
  const getPerPersonPrice = () => {
    if (flight?.perPersonPrice) return Math.round(flight.perPersonPrice);
    if (typeof flight?.price === 'number') {
      if (flight.price > 1000 && passengers > 1) {
        return Math.round(flight.price / passengers);
      }
      return Math.round(flight.price);
    }
    if (typeof flight?.price === 'object') {
      return Math.round(flight.price.perPerson || flight.price.total / passengers || 0);
    }
    if (flight?.totalPrice) {
      return Math.round(flight.totalPrice / passengers);
    }
    return 0;
  };
  
  const getTotalPrice = () => {
    if (flight?.totalPrice) return Math.round(flight.totalPrice);
    if (typeof flight?.price === 'number') return Math.round(flight.price);
    if (typeof flight?.price === 'object') return Math.round(flight.price.total || 0);
    return getPerPersonPrice() * passengers;
  };
  
  const perPersonPrice = getPerPersonPrice();
  const totalPrice = getTotalPrice();
  const source = flight?.source || 'amadeus';
  
  // Get booking options
  const getBookingOptions = () => {
    if (flight?.bookingOptions && flight.bookingOptions.length > 0) {
      return flight.bookingOptions.map((opt) => ({
        id: opt.id,
        name: opt.name,
        logo: opt.logo,
        isImage: true,
        price: opt.price || perPersonPrice,
        priceLabel: opt.priceLabel || (opt.price ? `$${opt.price}` : `$${perPersonPrice}`),
        isBest: opt.isBest,
        url: opt.url,
        badge: opt.isBest ? 'Best Price' : null,
        isDeepLink: opt.isDeepLink
      }));
    }
    
    if (flight?.bookingLinks && flight.bookingLinks.length > 0) {
      return flight.bookingLinks.map((link, idx) => ({
        id: link.name?.toLowerCase() || `link-${idx}`,
        name: link.name || 'Book',
        logo: link.logo || 'https://pics.avs.io/70/70/@s.png',
        isImage: true,
        price: perPersonPrice,
        priceLabel: `$${perPersonPrice}`,
        isBest: idx === 0,
        url: link.url,
        badge: idx === 0 ? 'Best Price' : null,
        isDeepLink: link.affiliate || false
      }));
    }
    
    return [{
      id: 'search',
      name: 'Search Flights',
      logo: 'https://pics.avs.io/70/70/@s.png',
      isImage: true,
      price: perPersonPrice,
      priceLabel: perPersonPrice ? `$${perPersonPrice}` : 'Search',
      isBest: true,
      url: flight?.deepLink || '#',
      badge: null,
      isDeepLink: false
    }];
  };
  
  const bookingOptions = getBookingOptions();
  const displayedOptions = showAllOptions ? bookingOptions : bookingOptions.slice(0, 4);
  
  // Format time - handle both "10:30 AM" and ISO datetime formats
  const formatTime = (timeStr) => {
    if (!timeStr) return '--:--';
    if (timeStr.includes('AM') || timeStr.includes('PM')) return timeStr;
    if (timeStr.includes('T')) {
      try {
        const date = new Date(timeStr);
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      } catch { return timeStr; }
    }
    return timeStr;
  };
  
  // Format date for leg label (Jan 3)
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return dateStr; }
  };
  
  // Check if arrival is next day
  const isNextDay = (departureTime, arrivalTime) => {
    if (!departureTime || !arrivalTime) return false;
    if (departureTime.includes('T') && arrivalTime.includes('T')) {
      const dep = new Date(departureTime);
      const arr = new Date(arrivalTime);
      return arr.getDate() !== dep.getDate();
    }
    return false;
  };

  // Handle booking click
  const handleBookClick = (option, e) => {
    e.stopPropagation();
    if (option.url && option.url !== '#') {
      window.open(option.url, '_blank', 'noopener,noreferrer');
    }
    if (onBook) onBook({ flight, option });
  };

  // Get airline info from leg
  const getAirlineName = (leg) => {
    if (!leg) return 'Airline';
    if (typeof leg.airline === 'string') return leg.airline;
    if (typeof leg.airline === 'object') return leg.airline.name || leg.airline.code || 'Airline';
    return leg.carrierName || 'Airline';
  };
  
  const getAirlineCode = (leg) => {
    if (!leg) return '';
    if (leg.airlineCode) return leg.airlineCode;
    if (typeof leg.airline === 'object') return leg.airline.code || '';
    if (leg.carrier) return leg.carrier;
    return '';
  };

  // Get stops text
  const getStopsText = (leg) => {
    if (!leg) return 'Nonstop';
    if (leg.stopsText) return leg.stopsText;
    if (typeof leg.stops === 'string') return leg.stops;
    if (typeof leg.stops === 'number') {
      if (leg.stops === 0) return 'Nonstop';
      if (leg.stops === 1) return '1 stop';
      return `${leg.stops} stops`;
    }
    if (leg.stopsCount !== undefined) {
      if (leg.stopsCount === 0) return 'Nonstop';
      if (leg.stopsCount === 1) return '1 stop';
      return `${leg.stopsCount} stops`;
    }
    return 'Nonstop';
  };

  // Get departure/arrival times
  const getDepartureTime = (leg) => leg?.departureTime || leg?.departure || '';
  const getArrivalTime = (leg) => leg?.arrivalTime || leg?.arrival || '';
  const getOrigin = (leg) => leg?.origin || leg?.from || '--';
  const getDestination = (leg) => leg?.destination || leg?.to || '--';

  // ═══════════════════════════════════════════════════════════════════════════════
  // STYLES - Matching Mockup Exactly
  // ═══════════════════════════════════════════════════════════════════════════════
  const styles = {
    card: {
      background: '#ffffff',
      borderRadius: '16px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
      marginBottom: '16px',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      position: 'relative'
    },
    bestDealBadge: {
      position: 'absolute',
      top: '12px',
      left: '12px',
      background: 'linear-gradient(145deg, #fbbf24 0%, #f59e0b 100%)',
      color: '#18181b',
      fontSize: '11px',
      fontWeight: '700',
      padding: '6px 12px',
      borderRadius: '6px',
      zIndex: 1,
      boxShadow: '0 2px 4px rgba(251, 191, 36, 0.3)'
    },
    flightLeg: {
      padding: '16px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: '20px'
    },
    flightLegReturn: {
      padding: '16px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      borderTop: '1px dashed #e5e7eb'
    },
    airlineSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      minWidth: '140px'
    },
    airlineLogo: {
      width: '44px',
      height: '44px',
      background: '#f4f4f5',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      flexShrink: 0
    },
    airlineName: {
      fontWeight: '600',
      fontSize: '13px',
      color: '#18181b'
    },
    flightNumber: {
      fontSize: '11px',
      color: '#71717a'
    },
    flightRoute: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    timeBlock: {
      textAlign: 'center',
      minWidth: '80px'
    },
    time: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#18181b'
    },
    timeNextDay: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#18181b',
      position: 'relative'
    },
    nextDayBadge: {
      fontSize: '10px',
      fontWeight: '600',
      color: '#f59e0b',
      marginLeft: '2px',
      verticalAlign: 'super'
    },
    airport: {
      fontSize: '12px',
      color: '#71717a'
    },
    flightPath: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    duration: {
      fontSize: '11px',
      color: '#71717a',
      marginBottom: '6px'
    },
    pathLine: {
      width: '100%',
      height: '2px',
      background: '#e5e7eb',
      position: 'relative'
    },
    pathDot: {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '8px',
      height: '8px',
      background: '#fbbf24',
      borderRadius: '50%'
    },
    stopsNonstop: {
      fontSize: '10px',
      marginTop: '6px',
      padding: '2px 8px',
      borderRadius: '10px',
      background: '#ecfdf5',
      color: '#059669',
      fontWeight: '500'
    },
    stopsHas: {
      fontSize: '10px',
      marginTop: '6px',
      padding: '2px 8px',
      borderRadius: '10px',
      background: '#fef3c7',
      color: '#92400e',
      fontWeight: '500'
    },
    priceSection: {
      textAlign: 'right',
      minWidth: '120px',
      borderLeft: '1px solid #e5e7eb',
      paddingLeft: '20px'
    },
    priceLabel: {
      fontSize: '10px',
      color: '#a1a1aa'
    },
    priceAmount: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#059669'
    },
    pricePer: {
      fontSize: '10px',
      color: '#71717a'
    },
    cardFooter: {
      padding: '12px 20px',
      background: '#fafafa',
      borderTop: '1px solid #f4f4f5',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    sourceLabel: {
      fontSize: '10px',
      color: '#a1a1aa'
    },
    bookBtn: {
      padding: '10px 20px',
      background: 'linear-gradient(145deg, #fbbf24 0%, #f59e0b 100%)',
      border: 'none',
      borderRadius: '10px',
      fontWeight: '700',
      fontSize: '13px',
      color: '#18181b',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    // Multi-passenger total section
    totalSection: {
      padding: '10px 20px',
      background: '#f8fafc',
      borderTop: '1px solid #f4f4f5',
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: '8px',
      fontSize: '12px',
      color: '#64748b'
    },
    totalAmount: {
      fontWeight: '700',
      color: '#18181b'
    }
  };

  const renderFlightLeg = (leg, isOutbound, isReturn = false) => {
    const airlineCode = getAirlineCode(leg);
    const airlineName = getAirlineName(leg);
    const depTime = getDepartureTime(leg);
    const arrTime = getArrivalTime(leg);
    const stopsText = getStopsText(leg);
    const isNonstop = stopsText === 'Nonstop';
    const nextDay = isNextDay(depTime, arrTime);
    const legDate = formatDate(leg?.departureDate || (isOutbound ? lastSearchParams?.departureDate : lastSearchParams?.returnDate));
    const origin = getOrigin(leg);
    const dest = getDestination(leg);
    const duration = leg?.duration || '';
    
    return (
      <div style={isReturn ? styles.flightLegReturn : styles.flightLeg}>
        {/* Airline Section */}
        <div style={styles.airlineSection}>
          <div style={styles.airlineLogo}>
            {airlineCode ? (
              <img 
                src={`https://pics.avs.io/70/70/${airlineCode}.png`}
                alt={airlineName}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '✈️'; }}
              />
            ) : (
              <span style={{ fontSize: '1.25rem' }}>✈️</span>
            )}
          </div>
          <div>
            <div style={styles.airlineName}>{airlineName}</div>
            <div style={styles.flightNumber}>{isOutbound ? 'Outbound' : 'Return'}: {leg?.flightNumber || ''}</div>
          </div>
        </div>
        
        {/* Flight Route */}
        <div style={styles.flightRoute}>
          <div style={styles.timeBlock}>
            <div style={styles.time}>{formatTime(depTime)}</div>
            <div style={styles.airport}>{origin} → {dest}</div>
          </div>
          
          <div style={styles.flightPath}>
            <div style={styles.duration}>{legDate}</div>
            <div style={styles.pathLine}>
              <span style={{...styles.pathDot, left: 0}}></span>
              <span style={{...styles.pathDot, right: 0}}></span>
            </div>
            <div style={isNonstop ? styles.stopsNonstop : styles.stopsHas}>
              {stopsText} {duration && `• ${duration}`}
            </div>
          </div>
          
          <div style={styles.timeBlock}>
            <div style={nextDay ? styles.timeNextDay : styles.time}>
              {formatTime(arrTime)}
              {nextDay && <span style={styles.nextDayBadge}>+1</span>}
            </div>
            <div style={styles.airport}>Arrive</div>
          </div>
        </div>
        
        {/* Price Section - Only show on outbound */}
        {isOutbound && (
          <div style={styles.priceSection}>
            <div style={styles.priceLabel}>Round-trip from</div>
            <div style={styles.priceAmount}>${perPersonPrice.toLocaleString()}</div>
            <div style={styles.pricePer}>per person</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.card}>
      {/* Best Deal Badge */}
      {isBestDeal && (
        <span style={styles.bestDealBadge}>🏆 Best Round-Trip</span>
      )}
      
      {/* Outbound Flight */}
      {renderFlightLeg(outbound, true, false)}
      
      {/* Return Flight */}
      {returnF && renderFlightLeg(returnF, false, true)}
      
      {/* Multi-passenger total */}
      {passengers > 1 && (
        <div style={styles.totalSection}>
          <span>{passengers} passengers × ${perPersonPrice.toLocaleString()} =</span>
          <span style={styles.totalAmount}>${totalPrice.toLocaleString()} total</span>
        </div>
      )}
      
      {/* Footer with Book Button */}
      <div style={styles.cardFooter}>
        <div style={styles.sourceLabel}>
          via {source === 'travelpayouts' ? 'Aviasales' : source === 'amadeus' ? 'Amadeus' : source}
        </div>
        <button 
          style={styles.bookBtn}
          onClick={(e) => handleBookClick(bookingOptions[0], e)}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-1px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
        >
          Book Round-Trip →
        </button>
      </div>
    </div>
  );
};

export default RoundTripFlightCard;
